import React, { useRef, forwardRef, useEffect } from "react"

import useTracking from "hooks/useTracking"
import { useLocation } from "store/contexts/location"

import StarsRating from "../stars-rating"
import {
  Container,
  Placeholder,
  ProductTitle,
  Description,
  Button,
  ClickElement,
  StarsAndDescriptionContainer,
  ArticleContainer,
  ImageContainer,
  TextContainer,
  ReadNext,
  ArticleTitle,
  AnchorContainer,
} from "./styles"

const BottomStickyProduct = forwardRef(({ element = { url: "/" }, isCurrentArticle }, ref) => {
  // Always render the placeholder to maintain layout
  if (!element || [139, 152].includes(Number(element.classId))) {
    return <Placeholder className="bottom-sticky-placeholder" />
  }

  const { location } = useLocation()

  const { relatedArticle } = element
  const displayRelatedArticle = relatedArticle && !relatedArticle.url.includes(location.pathname)

  const { trackView, trackClick } = useTracking()
  const refProductView = useRef(null)
  const refProductClick = useRef(null)
  const refArticleView = useRef(null)
  const refArticleClick = useRef(null)

  const trackingId = displayRelatedArticle
    ? `Sticky Related Article | ${element.relatedArticle.title.markdown}`
    : `Article Sticky Product | ${element.title}`
  const trackingProps = {
    label: trackingId,
    category: "Commerce Engagement",
    product: element.trackProduct,
    product_id: element.productId,
    autoplacement: Boolean(element.autoplacement),
    testimonial: Boolean(element.useTestimonial),
    class_id: element.classId,
  }

  trackView(displayRelatedArticle ? refArticleView : refProductView, trackingId, trackingProps)
  trackClick(displayRelatedArticle ? refArticleClick : refProductClick, trackingId, trackingProps)

  const getDefaultButtonText = () => {
    if (!element.productId && !element.classId) {
      return "Get started"
    }

    if (element.classId) {
      return "Learn More"
    }

    return "Shop Now"
  }

  // Always render the placeholder
  // Only render the actual product if this is the current article
  if (!isCurrentArticle) {
    return <Placeholder className="bottom-sticky-placeholder" />
  }

  if (displayRelatedArticle) {
    return (
      <>
        <Placeholder className="bottom-sticky-placeholder" />
        <ArticleContainer ref={refArticleView} data-exp-id="product-sticky">
          <AnchorContainer ref={refArticleClick} href={relatedArticle.url}>
            <ImageContainer
              fetchpriority="low"
              baseUrl={relatedArticle.image}
              images={[
                {
                  screenSize: 768,
                  transforms: "c_fill,g_auto,w_100,h_70,f_auto,fl_lossy,q_auto",
                },
                {
                  transforms: "c_fill,g_auto,w_70,h_70,f_auto,fl_lossy,q_auto",
                },
              ]}
            />
            <TextContainer>
              <ReadNext>READ NEXT</ReadNext>
              <ArticleTitle>{relatedArticle.title.markdown}</ArticleTitle>
            </TextContainer>
          </AnchorContainer>
        </ArticleContainer>
      </>
    )
  }

  return (
    <>
      <Placeholder className="bottom-sticky-placeholder" />
      <Container ref={ref} className="bottom-sticky-product" data-exp-id="product-sticky">
        <ClickElement ref={refProductClick} href={element.url} rel="sponsored" target="_blank">
          <ProductTitle
            dangerouslySetInnerHTML={{
              __html: element.title,
            }}
          />
          <StarsAndDescriptionContainer>
            <Description
              dangerouslySetInnerHTML={{
                __html: element.description,
              }}
            />
            <StarsRating rating={element.ratingP} renderOnMobile={false} />
          </StarsAndDescriptionContainer>
          <Button custom={!element.productId && !element.isClass} isClass={element.isClass}>
            {element.buttonText || getDefaultButtonText()}
          </Button>
        </ClickElement>
      </Container>
    </>
  )
})

BottomStickyProduct.displayName = "BottomStickyProduct"

export default BottomStickyProduct
